import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

import { Button, ButtonTypes } from 'src/presentation/components/agent_desktop_components';
import { ExternalSourceType, ExternalSourceTypes } from 'src/actions/integrations/commonTypes';
import {
  getOAuthRedirectURL,
  redirectToUrl,
} from 'src/presentation/components/pages/integrations/editorComponents/helpers';
import { integrationsOrgConfig } from 'src/actions/integrations/integrationsOrgConfigResource';
import PopoverMenu, { StyledMenuItem, usePopoverMenu } from '@ad-scripts/presentation/components/common/menu';
import { SHOPIFY_APPSTORE_REDIRECT_URL } from 'src/presentation/components/pages/integrations/constants';
import { StyledConfirmOrCancelModal } from 'src/presentation/components/pages/shared/rowActions/StyledConfirmOrCancelModal';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';

type IntegrationRedirectType = typeof ExternalSourceTypes.Shopify | typeof ExternalSourceTypes.Yotpo | null;

interface CreateIntegrationDropdownProps {
  root: string;
  orgConfig?: integrationsOrgConfig;
}

const CreateIntegrationDropdown = ({ root, orgConfig }: CreateIntegrationDropdownProps): JSX.Element => {
  const integrationRedirect = useRef<IntegrationRedirectType>(null);
  const isFeatureEnabled = useIsFeatureEnabled();
  const [redirectPromptVisible, setRedirectPromptVisible] = useState(false);
  const { targetRef, setTargetRef, isOpen, onClose, onToggle } = usePopoverMenu();
  const dispatch = useDispatch();
  const useShopifyRedirect = !orgConfig?.isShopifyPrivateAppCustomer;

  const makeTargetUrl = (lookupType: ExternalSourceType) => `${root}/new?type=${lookupType.toLowerCase()}`;

  const yotpoMenuItem = isFeatureEnabled('yotpo') ? (
    <StyledMenuItem key={ExternalSourceTypes.Yotpo} data-aid="create-integration-yotpo-menuitem" onClick={onClose}>
      <StyledMenuLink
        to="#"
        onClick={() => {
          setRedirectPromptVisible(true);
          integrationRedirect.current = ExternalSourceTypes.Yotpo;
        }}
      >
        Yotpo
      </StyledMenuLink>
    </StyledMenuItem>
  ) : null;

  let redirectConfirmModal;
  if (redirectPromptVisible) {
    switch (integrationRedirect.current) {
      // Build confirmation dialog that will be displayed before redirecting to Shopify App Store
      case ExternalSourceTypes.Shopify:
        redirectConfirmModal = (
          <StyledConfirmOrCancelModal
            data-aid="shopify-redirect-prompt"
            onCancel={() => {
              setRedirectPromptVisible(false);
              integrationRedirect.current = null;
            }}
            onSubmit={() => redirectToUrl(SHOPIFY_APPSTORE_REDIRECT_URL, setRedirectPromptVisible, dispatch)}
            submitButtonType={ButtonTypes.PRIMARY}
            submitText="Continue to Shopify"
            text={
              <span>
                We&apos;ll take you to the Shopify App Store
                <br />
                where you can set up the app.
              </span>
            }
            title="Add Shopify"
          />
        );
        break;

      // Build confirmation dialog that will be displayed before redirecting to Yotpo
      case ExternalSourceTypes.Yotpo:
        redirectConfirmModal = (
          <StyledConfirmOrCancelModal
            data-aid="yotpo-redirect-prompt"
            onCancel={() => {
              setRedirectPromptVisible(false);
              integrationRedirect.current = null;
            }}
            onSubmit={() =>
              redirectToUrl(getOAuthRedirectURL(ExternalSourceTypes.Yotpo), setRedirectPromptVisible, dispatch)
            }
            submitButtonType={ButtonTypes.PRIMARY}
            submitText="Continue to Yotpo"
            text={
              <span>
                We&apos;ll take you to the Yotpo Configuration Page
                <br />
                where you can begin the authentication process.
              </span>
            }
            title="Add Yotpo"
          />
        );
        break;

      default:
        redirectConfirmModal = null;
        break;
    }
  }

  /*
    Keep in alphabetical order
  */
  return (
    <>
      <Button data-aid="create-integration-button" type={ButtonTypes.PRIMARY} onClick={onToggle} ref={setTargetRef}>
        Add App
      </Button>
      <StyledMenu
        data-aid="create-integration-dropdown"
        isOpen={isOpen}
        onClose={onClose}
        position="bottom"
        targetPosition="end"
        targetRef={targetRef}
      >
        <StyledMenuItem
          key={ExternalSourceTypes.AwsEventBridge}
          data-aid="create-integration-awseventbridge-menuitem"
          onClick={onClose}
        >
          <StyledMenuLink to={makeTargetUrl(ExternalSourceTypes.AwsEventBridge)}>AWS EventBridge</StyledMenuLink>
        </StyledMenuItem>

        <StyledMenuItem
          key={ExternalSourceTypes.BigCommerce}
          data-aid="create-integration-bigcommerce-menuitem"
          onClick={onClose}
        >
          <StyledMenuLink to={makeTargetUrl(ExternalSourceTypes.BigCommerce)}>BigCommerce</StyledMenuLink>
        </StyledMenuItem>

        <StyledMenuItem
          key={ExternalSourceTypes.Klaviyo}
          data-aid="create-integration-klaviyo-menuitem"
          onClick={onClose}
        >
          <StyledMenuLink to={makeTargetUrl(ExternalSourceTypes.Klaviyo)}>Klaviyo</StyledMenuLink>
        </StyledMenuItem>

        <StyledMenuItem
          key={ExternalSourceTypes.Magento}
          data-aid="create-integration-magento-menuitem"
          onClick={onClose}
        >
          <StyledMenuLink to={makeTargetUrl(ExternalSourceTypes.Magento)}>Magento</StyledMenuLink>
        </StyledMenuItem>

        <StyledMenuItem key={ExternalSourceTypes.Zaius} data-aid="create-integration-zaius-menuitem" onClick={onClose}>
          <StyledMenuLink to={makeTargetUrl(ExternalSourceTypes.Zaius)}>Optimizely</StyledMenuLink>
        </StyledMenuItem>

        <StyledMenuItem
          key={ExternalSourceTypes.Ordergroove}
          data-aid="create-integration-ordergroove-menuitem"
          onClick={onClose}
        >
          <StyledMenuLink to={makeTargetUrl(ExternalSourceTypes.Ordergroove)}>Ordergroove</StyledMenuLink>
        </StyledMenuItem>

        <StyledMenuItem
          key={ExternalSourceTypes.Qualtrics}
          data-aid="create-integration-qualtrics-menuitem"
          onClick={onClose}
        >
          <StyledMenuLink to={makeTargetUrl(ExternalSourceTypes.Qualtrics)}>Qualtrics</StyledMenuLink>
        </StyledMenuItem>

        <StyledMenuItem
          key={ExternalSourceTypes.Recharge}
          data-aid="create-integration-recharge-menuitem"
          onClick={onClose}
        >
          <StyledMenuLink to={makeTargetUrl(ExternalSourceTypes.Recharge)}>Recharge</StyledMenuLink>
        </StyledMenuItem>

        <StyledMenuItem
          key={ExternalSourceTypes.Segment}
          data-aid="create-integration-segment-menuitem"
          onClick={onClose}
        >
          <StyledMenuLink to={makeTargetUrl(ExternalSourceTypes.Segment)}>Segment</StyledMenuLink>
        </StyledMenuItem>

        <StyledMenuItem
          key={ExternalSourceTypes.Shopify}
          data-aid="create-integration-shopify-menuitem"
          onClick={onClose}
        >
          {useShopifyRedirect ? (
            <StyledMenuLink
              to="#"
              onClick={() => {
                setRedirectPromptVisible(true);
                integrationRedirect.current = ExternalSourceTypes.Shopify;
              }}
            >
              Shopify
            </StyledMenuLink>
          ) : (
            <StyledMenuLink to={makeTargetUrl(ExternalSourceTypes.Shopify)}>Shopify</StyledMenuLink>
          )}
        </StyledMenuItem>

        <StyledMenuItem key={ExternalSourceTypes.Smile} data-aid="create-integration-smile-menuitem" onClick={onClose}>
          <StyledMenuLink to={makeTargetUrl(ExternalSourceTypes.Smile)}>Smile</StyledMenuLink>
        </StyledMenuItem>

        {yotpoMenuItem}

        <StyledMenuItem
          key={ExternalSourceTypes.Custom}
          data-aid="create-integration-custom-menuitem"
          onClick={onClose}
        >
          <StyledMenuLink to={makeTargetUrl(ExternalSourceTypes.Custom)}>Custom</StyledMenuLink>
        </StyledMenuItem>
      </StyledMenu>
      {redirectConfirmModal}
    </>
  );
};

const StyledMenu = styled(PopoverMenu)`
  border: 1px solid ${p => p.theme.colors.gray200};
  min-width: 100px;
`;

const StyledMenuLink = styled(Link)`
  color: ${p => p.theme.colors.black};
  text-decoration: none;
`;

export default CreateIntegrationDropdown;
